import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "src/common/state/hooks";
import type { RagDocument } from "../api/ragApi";
import { fetchOrgDocuments, assignDocumentToAgent } from "../api/ragApi";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import { openStandardModal, closeStandardModal } from "src/common/state/slice/modal/modalSlice";
import { SearchInput } from "src/common/components/SearchInput";
import { setAlert } from "src/features/alert/state/alertsSlice";

const statusConfig: Record<string, { dot: string }> = {
  processing: { dot: "bg-yellow-400 animate-pulse" },
  ready: { dot: "bg-green-500" },
  error: { dot: "bg-red-500" },
};

const formatSize = (bytes: number | string) => {
  const b = Number(bytes);
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / (1024 * 1024)).toFixed(1)} Mo`;
};

interface DocumentLibraryPickerProps {
  agentId: number;
  currentDocumentIds: number[];
  onAttached: () => void;
}

const MODAL_ID = "doc-library-picker";

const DocumentLibraryPicker = ({
  agentId,
  currentDocumentIds,
  onAttached,
}: DocumentLibraryPickerProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [orgDocs, setOrgDocs] = useState<RagDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [attaching, setAttaching] = useState<number | null>(null);

  useEffect(() => {
    fetchOrgDocuments()
      .then((docs) => setOrgDocs(docs))
      .catch((err) => console.error("Failed to load org documents:", err))
      .finally(() => setLoading(false));
  }, []);

  const filteredDocs = useMemo(() => {
    const q = search.toLowerCase().trim();
    const docs = q ? orgDocs.filter((d) => d.filename.toLowerCase().includes(q)) : orgDocs;
    return [...docs].sort((a, b) => {
      const aAttached = currentDocumentIds.includes(a.id) ? 1 : 0;
      const bAttached = currentDocumentIds.includes(b.id) ? 1 : 0;
      return aAttached - bAttached;
    });
  }, [orgDocs, search, currentDocumentIds]);

  const handleAttach = async (docId: number) => {
    setAttaching(docId);
    try {
      await assignDocumentToAgent(docId, agentId);
      dispatch(setAlert({ type: "success", msg: t("pages.agents.documentAssigned") }));
      onAttached();
    } catch (err) {
      console.error("Failed to attach document:", err);
    }
    setAttaching(null);
  };

  return (
    <>
      <button
        onClick={() => dispatch(openStandardModal(MODAL_ID))}
        className="inline-flex items-center gap-1.5 text-xs text-green-600 hover:text-green-700 font-medium transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
          />
        </svg>
        {t("pages.agents.attachFromLibrary")}
      </button>

      <ModalStandard
        id={MODAL_ID}
        fullScreen={false}
        showCloseButton
        minimize
        className="w-full max-w-lg h-fit"
      >
        <h3 className="font-semibold text-base text-gray-900 mb-3 pr-6">
          {t("pages.agents.documentLibraryPicker")}
        </h3>

        {!loading && orgDocs.length > 0 && (
          <div className="mb-2">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder={t("general.search") + "..."}
            />
            <p className="text-xs text-gray-400 mt-1.5 tabular-nums">
              {filteredDocs.length} / {orgDocs.length}
              {search.trim() && ` — "${search.trim()}"`}
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-6">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : orgDocs.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">
            {t("pages.agents.noLibraryDocuments")}
          </p>
        ) : filteredDocs.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">
            {t("statsNew.emptyState.noSearchResults")}
          </p>
        ) : (
          <div className="flex flex-col gap-1.5 max-h-[380px] overflow-y-auto">
            {filteredDocs.map((doc) => {
              const isAttached = currentDocumentIds.includes(doc.id);
              const isAttaching = attaching === doc.id;
              const cfg = statusConfig[doc.status] || statusConfig.error;
              return (
                <div
                  key={doc.id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border text-sm ${
                    isAttached
                      ? "border-gray-200 bg-gray-50 opacity-60"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-900 font-medium truncate block text-sm">
                      {doc.filename}
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatSize(doc.size_bytes)}
                      {doc.chunk_count > 0 && ` · ${doc.chunk_count} chunks`}
                    </span>
                  </div>
                  {isAttached ? (
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {t("pages.agents.alreadyAttached")}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAttach(doc.id)}
                      disabled={isAttaching || doc.status === "error"}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {isAttaching ? (
                        <span className="w-3 h-3 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-3 w-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      )}
                      {t("pages.agents.attach")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end mt-4 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={() => dispatch(closeStandardModal(MODAL_ID))}
            className="btn-alternative-outline text-sm"
          >
            {t("general.close")}
          </button>
        </div>
      </ModalStandard>
    </>
  );
};

export default DocumentLibraryPicker;
